// Light plus three Discord-style dark themes (progressively darker).
// Each dark theme keeps the same layers: rail → nav → page → card.

export const lightTheme = {
  name: 'light',
  isDark: false,
  label: 'Light',
  rail: '#101010',
  nav: '#1c1c1c',
  page: '#ffffff',
  card: '#ffffff',
  inset: '#ffffff',
  hover: '#f6f6f6',
  surfaceAlt: '#fafafa',
  text: '#000000',
  textMuted: '#333333',
  textFaint: '#999999',
  border: 'rgba(0, 0, 0, 0.12)',
  borderSubtle: 'rgba(0, 0, 0, 0.08)',
  accent: '#0e4afb',
  accentSoft: 'rgba(14, 74, 251, 0.12)',
  accentContrast: '#ffffff',
  success: '#00B56A',
  header: '#06170D',
  headerAccent: '#83F5AD',
  headerText: '#FFFFFF',
  headerMuted: 'rgba(131, 245, 173, 0.9)',
  headerControl: 'rgba(255, 255, 255, 0.08)',
  headerHeight: '56px',
  chromeRadius: '22px',
  danger: '#e94235',
};

export const darkTheme = {
  name: 'dark',
  isDark: true,
  label: 'Dark',
  rail: '#1e1f22',
  nav: '#2b2d31',
  page: '#313338',
  card: '#383a40',
  inset: '#1e1f22',
  hover: '#404249',
  surfaceAlt: '#2e3035',
  text: '#dbdee1',
  textMuted: '#b5bac1',
  textFaint: '#949ba4',
  border: 'rgba(255, 255, 255, 0.14)',
  borderSubtle: 'rgba(255, 255, 255, 0.1)',
  accent: '#0e4afb',
  accentSoft: 'rgba(14, 74, 251, 0.24)',
  accentContrast: '#06170D',
  success: '#00B56A',
  header: '#1e1f22',
  headerAccent: '#dbdee1',
  headerText: '#FFFFFF',
  headerMuted: 'rgba(219, 222, 225, 0.85)',
  headerControl: 'rgba(255, 255, 255, 0.1)',
  headerHeight: '56px',
  chromeRadius: '22px',
  danger: '#ed4245',
};

export const darkerTheme = {
  name: 'darker',
  isDark: true,
  label: 'Darker',
  rail: '#0a0a0b',
  nav: '#141517',
  page: '#17181b',
  card: '#1e1f22',
  inset: '#0a0a0b',
  hover: '#26272b',
  surfaceAlt: '#17181b',
  text: '#dbdee1',
  textMuted: '#b5bac1',
  textFaint: '#949ba4',
  border: 'rgba(255, 255, 255, 0.14)',
  borderSubtle: 'rgba(255, 255, 255, 0.1)',
  accent: '#0e4afb',
  accentSoft: 'rgba(14, 74, 251, 0.24)',
  accentContrast: '#06170D',
  success: '#00B56A',
  header: '#0a0a0b',
  headerAccent: '#dbdee1',
  headerText: '#FFFFFF',
  headerMuted: 'rgba(219, 222, 225, 0.8)',
  headerControl: 'rgba(255, 255, 255, 0.08)',
  headerHeight: '56px',
  chromeRadius: '22px',
  danger: '#ed4245',
};

export const midnightTheme = {
  name: 'midnight',
  isDark: true,
  label: 'Midnight',
  rail: '#000000',
  nav: '#020202',
  page: '#000000',
  card: '#040404',
  inset: '#000000',
  hover: '#080808',
  surfaceAlt: '#020202',
  text: '#dbdee1',
  textMuted: '#b5bac1',
  textFaint: '#8a8a8a',
  border: 'rgba(255, 255, 255, 0.16)',
  borderSubtle: 'rgba(255, 255, 255, 0.12)',
  accent: '#0e4afb',
  accentSoft: 'rgba(14, 74, 251, 0.24)',
  accentContrast: '#06170D',
  success: '#00B56A',
  header: '#080808',
  headerAccent: '#dbdee1',
  headerText: '#FFFFFF',
  headerMuted: 'rgba(219, 222, 225, 0.75)',
  headerControl: 'rgba(255, 255, 255, 0.06)',
  headerHeight: '56px',
  chromeRadius: '22px',
  danger: '#ed4245',
};

const withAliases = (theme) => ({
  ...theme,
  chrome: theme.header,
  sidebar: theme.nav,
  surface: theme.card,
  surfaceAlt: theme.surfaceAlt,
  surfaceHover: theme.hover,
  button: theme.isDark ? theme.text : theme.header,
  buttonText: theme.isDark ? theme.page : theme.headerAccent,
  skeleton: theme.isDark ? 'rgba(255, 255, 255, 0.1)' : '#ececec',
  skeletonShine: theme.isDark ? 'rgba(255, 255, 255, 0.18)' : '#f7f7f7',
});

export const themesByName = {
  light: withAliases(lightTheme),
  dark: withAliases(darkTheme),
  darker: withAliases(darkerTheme),
  midnight: withAliases(midnightTheme),
};

export const THEME_OPTIONS = ['light', 'dark', 'darker', 'midnight'].map(
  (id) => {
    const theme = themesByName[id];
    return {
      id,
      label: theme.label,
      rail: theme.rail,
      nav: theme.nav,
      page: theme.page,
    };
  }
);

export const THEME_STORAGE_KEY = 'tol-theme';
export const ACCENT_STORAGE_KEY = 'tol-accent';
export const RADIUS_STORAGE_KEY = 'tol-radius';

export const ACCENT_OPTIONS = [
  { id: 'mint', label: 'Green', color: '#83F5AD' },
  { id: 'sky', label: 'Blue', color: '#7EB6FF' },
  { id: 'gold', label: 'Gold', color: '#F0C36A' },
  { id: 'rose', label: 'Rose', color: '#F5A3B5' },
  { id: 'white', label: 'White', color: '#FFFFFF' },
];

export const RADIUS_OPTIONS = [
  { id: 'sharp', label: 'Sharp', chrome: 0, card: 4, control: 4 },
  { id: 'soft', label: 'Soft', chrome: 8, card: 8, control: 6 },
  { id: 'round', label: 'Round', chrome: 16, card: 12, control: 8 },
  { id: 'full', label: 'Very round', chrome: 22, card: 16, control: 10 },
];

export const isRadiusId = (value) =>
  RADIUS_OPTIONS.some((option) => option.id === value);

export const applyRadius = (theme, radiusId) => {
  const option =
    RADIUS_OPTIONS.find((item) => item.id === radiusId) ||
    RADIUS_OPTIONS.find((item) => item.id === 'full');
  return {
    ...theme,
    radiusId: option.id,
    chromeRadius: `${option.chrome}px`,
    cardRadius: `${option.card}px`,
    controlRadius: `${option.control}px`,
  };
};

const hexToRgba = (hex, alpha) => {
  const raw = String(hex || '').replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((char) => char + char)
          .join('')
      : raw;
  const value = parseInt(full, 16);
  if (!Number.isFinite(value)) return `rgba(255, 255, 255, ${alpha})`;
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const applyChromeAccent = (theme, accentId) => {
  if (!theme?.isDark) return theme;
  const option =
    ACCENT_OPTIONS.find((item) => item.id === accentId) || ACCENT_OPTIONS[0];
  const color = option.color;
  const next = withAliases({
    ...theme,
    headerAccent: color,
    headerMuted: hexToRgba(color, 0.88),
    headerControl: hexToRgba(color, 0.18),
    accent: color,
    accentSoft: hexToRgba(color, 0.22),
    accentContrast: '#06170D',
  });
  return {
    ...next,
    button: color,
    buttonText: '#06170D',
  };
};
